import React, { useEffect, useState } from 'react';
import { Mail, Save } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

export const AdminUserEmailDialog = ({ user, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [loginEmail, setLoginEmail] = useState(user.email || '');
  const [recoveryEmail, setRecoveryEmail] = useState(user.recovery_email || '');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoginEmail(user.email || '');
    setRecoveryEmail(user.recovery_email || '');
  }, [open, user.email, user.recovery_email]);
  const save = async () => {
    const login = loginEmail.trim().toLowerCase();
    const recovery = recoveryEmail.trim().toLowerCase();
    if (!login) return toast.error('Login email is required.');
    setBusy(true);
    try {
      const result = await adminApi.updateUserEmails(user.user_id, {
        login_email: login,
        recovery_email: recovery || null,
      });
      toast.success(result.sessions_revoked ? `Emails updated; ${result.sessions_revoked} session(s) revoked.` : 'User emails updated.');
      setOpen(false);
      onUpdated?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not update user emails.');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(next) => !busy && setOpen(next)}>
      <DialogTrigger asChild><Button size="sm" variant="ghost" className="h-7 gap-1 text-[11.5px]" data-testid={`admin-edit-emails-${user.user_id}`}><Mail className="h-3.5 w-3.5" /> Emails</Button></DialogTrigger>
      <DialogContent className="max-w-md" data-testid="admin-user-email-dialog">
        <DialogHeader><DialogTitle>Edit user emails</DialogTitle><DialogDescription>Changes apply immediately. Updating the login email signs the user out of all active sessions.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2">
          <label className="block space-y-1.5 text-xs font-semibold">Login email<Input type="email" value={loginEmail} onChange={(event) => setLoginEmail(event.target.value)} data-testid="admin-user-login-email-input" /></label>
          <label className="block space-y-1.5 text-xs font-semibold">Recovery email<Input type="email" value={recoveryEmail} onChange={(event) => setRecoveryEmail(event.target.value)} placeholder="Optional" data-testid="admin-user-recovery-email-input" /></label>
        </div>
        <DialogFooter><Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid="admin-user-email-cancel">Cancel</Button><Button type="button" onClick={save} disabled={busy} data-testid="admin-user-email-save"><Save className="mr-1.5 h-3.5 w-3.5" /> Save emails</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};